import React, { FormEvent, useEffect, useState } from 'react';

import { Loader2, Send, ShieldCheck } from 'lucide-react';

import Modal from '@/components/common/Modal';
import type { PointsRechargeGiftRequest } from '@/types';

interface PointsGiftRechargeModalProps {
  open: boolean;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  onClose: () => void;
  onSubmit: (input: PointsRechargeGiftRequest) => Promise<void>;
}

const inputClass =
  'h-10 w-full rounded-lg border border-dark-border bg-dark-bg px-3 text-sm text-dark-text outline-none transition-colors placeholder:text-dark-text-secondary focus:border-theme-color disabled:cursor-not-allowed disabled:opacity-60';

const DEFAULT_GIFT_RECHARGE_REMARK = '积分赠送';

const parsePositiveInteger = (value: string): number | null => {
  const normalized = value.trim();
  if (!/^[1-9]\d*$/.test(normalized)) return null;
  return Number(normalized);
};

const parseUserIds = (value: string): number[] | null => {
  const parts = value
    .split(/[,，]/)
    .map((item) => item.trim())
    .filter(Boolean);
  if (parts.length === 0) return null;

  const userIds = parts.map(parsePositiveInteger);
  if (userIds.some((item) => item === null)) return null;

  return Array.from(new Set(userIds as number[]));
};

const PointsGiftRechargeModal: React.FC<PointsGiftRechargeModalProps> = ({
  open,
  loading,
  error,
  successMessage,
  onClose,
  onSubmit,
}) => {
  const [targetUserIds, setTargetUserIds] = useState('');
  const [allUsers, setAllUsers] = useState(false);
  const [points, setPoints] = useState('');
  const [remark, setRemark] = useState(DEFAULT_GIFT_RECHARGE_REMARK);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTargetUserIds('');
    setAllUsers(false);
    setPoints('');
    setRemark(DEFAULT_GIFT_RECHARGE_REMARK);
    setValidationError(null);
  }, [open]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const targetUserIdValues = allUsers ? [] : parseUserIds(targetUserIds);
    if (!allUsers && targetUserIdValues === null) {
      setValidationError('请输入有效的用户 UID，多个 UID 用逗号分隔');
      return;
    }

    const pointsValue = parsePositiveInteger(points);
    if (pointsValue === null) {
      setValidationError('请输入大于 0 的积分数量');
      return;
    }

    const remarkValue = remark.trim();
    if (!remarkValue) {
      setValidationError('请输入积分明细展示文案');
      return;
    }

    setValidationError(null);
    if (allUsers) {
      await onSubmit({
        allUsers: true,
        points: pointsValue,
        remark: remarkValue,
      });
      return;
    }

    const userIds = targetUserIdValues ?? [];
    await onSubmit({
      ...(userIds.length === 1 ? { userId: userIds[0] } : { userIds }),
      points: pointsValue,
      remark: remarkValue,
    });
  };

  const message = validationError || error;

  return (
    <Modal title="积分赠送" open={open} onClose={onClose} width="min(560px, calc(100vw - 48px))">
      <form className="space-y-4" onSubmit={handleSubmit}>
        <section className="rounded-lg border border-theme-color/30 bg-theme-color-10 px-4 py-3">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-dark-card text-theme-color">
              <ShieldCheck className="h-5 w-5" />
            </span>
            <div>
              <p className="text-sm font-medium text-dark-text">管理员操作</p>
              <p className="mt-1 text-xs text-dark-text-secondary">需要服务端鉴权通过后生效</p>
            </div>
          </div>
        </section>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-2 block text-sm font-medium text-dark-text">目标用户 UID</span>
            <input
              type="text"
              inputMode="text"
              value={targetUserIds}
              disabled={loading || allUsers}
              onChange={(event) => setTargetUserIds(event.target.value)}
              className={inputClass}
            />
            <span className="mt-1 block text-xs text-dark-text-secondary">
              支持输入多个 UID，用逗号分隔
            </span>
          </label>
          <label className="block">
            <span className="mb-2 block text-sm font-medium text-dark-text">赠送积分</span>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={points}
              disabled={loading}
              onChange={(event) => setPoints(event.target.value)}
              className={inputClass}
            />
          </label>
        </div>

        <label className="flex items-center gap-2 rounded-lg border border-dark-border bg-dark-card px-3 py-2 text-sm text-dark-text">
          <input
            type="checkbox"
            checked={allUsers}
            disabled={loading}
            onChange={(event) => setAllUsers(event.target.checked)}
            className="h-4 w-4 accent-theme-color"
          />
          <span>全部用户</span>
        </label>

        <div className="grid gap-3">
          <label className="block">
            <span className="mb-2 block text-sm font-medium text-dark-text">展示文案</span>
            <input
              type="text"
              value={remark}
              disabled={loading}
              onChange={(event) => setRemark(event.target.value)}
              className={inputClass}
              maxLength={80}
            />
          </label>
        </div>

        {message ? (
          <div className="rounded-lg border border-accent-red bg-accent-red-10 px-3 py-2 text-sm text-accent-red">
            {message}
          </div>
        ) : null}

        {successMessage ? (
          <div className="rounded-lg border border-theme-color/30 bg-theme-color-10 px-3 py-2 text-sm text-dark-text">
            {successMessage}
          </div>
        ) : null}

        <div className="flex items-center justify-end gap-3 pt-1">
          <button
            type="button"
            disabled={loading}
            onClick={onClose}
            className="inline-flex h-10 items-center justify-center rounded-lg border border-dark-border px-4 text-sm text-dark-text-secondary transition-colors hover:border-theme-color hover:text-dark-text disabled:cursor-not-allowed disabled:opacity-60"
          >
            取消
          </button>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-theme-color px-4 text-sm font-medium text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            确认赠送
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default PointsGiftRechargeModal;
